import { API_BASE_URL, apiClient } from './api.client';
import { clearSessionAndRedirect, isTokenExpired } from './api.client';
import { isUnavailabilityError } from './serverMonitor';
import { LoginRequest, RegisterRequest, AuthResponse, User, ForgotPasswordRequest, ForgotPasswordResponse, ResetPasswordRequest, PlatformLoginRequest, PlatformAuthResponse, CompanyLookup, LoginMode } from '../types/user.types';
import { seedContextIdentity } from '../hooks/useAppContext';
import { platformApiClient } from './platform.api.client';
import { clearPlatformPreferences } from './platformPreferences';

/**
 * The session token lives in MEMORY for the life of the page, and nowhere else.
 *
 * It is not written to localStorage, so a script running on the page cannot read it out of storage
 * and it does not survive the tab. What survives is the HttpOnly cookie the server sets, which the
 * browser attaches to requests and JavaScript cannot touch. After a refresh this is empty and the
 * cookie alone carries the session — which is the point.
 *
 * It is kept at all only because a handful of browser-initiated requests cannot be given a header:
 * an <img> loading a photo, a SignalR handshake, a beacon on unload. Those take the token in the
 * URL while it is in memory and fall back to the cookie once it is not.
 */
let sessionToken: string | null = null;

/**
 * The session, entirely in memory: who is signed in and until when. Nothing is written to the
 * browser. After a reload both are empty and the HttpOnly cookie is the only thing that knows there
 * is a session — so the app asks the server (/auth/me) before it renders anything guarded, and the
 * answer refills these. See rehydrateSessionAsync.
 */
let sessionExpiry: Date | null = null;
let sessionUser: User | null = null;

export function setSessionToken(token: string | null, expiresAt?: string | Date | null): void {
  sessionToken = token || null;
  if (token && expiresAt) sessionExpiry = new Date(expiresAt);
  else if (!token) sessionExpiry = null;
}

/** When the session expires, or null when nothing is signed in. */
export function getSessionExpiry(): Date | null {
  return sessionExpiry;
}

/** Establish the signed-in identity for this page. */
export function setSessionUser(user: User | null): void {
  sessionUser = user;
  seedContextIdentity(user?.companyId ?? null);
}

/** Forget the session entirely — logout, or a probe that found none. */
export function clearSession(): void {
  sessionToken = null;
  sessionExpiry = null;
  sessionUser = null;
}

/**
 * Ask the server who this is. The cookie goes with the request; a 200 means a live session and
 * refills identity and expiry, a 401 means there is none. This is the ONLY way the app learns it
 * is signed in after a reload, because nothing about the session is kept in the browser.
 */
export type RehydrateResult = 'session' | 'none' | 'unreachable';

/**
 * Who the cookie says we are. 'none' is the server's answer (no live session — go and sign in);
 * 'unreachable' is no answer at all, which says nothing about the session and must not be treated
 * as a sign-out: the cookie is still there for when the server comes back.
 */
export async function rehydrateSessionStateAsync(): Promise<RehydrateResult> {
  try {
    const res = await apiClient.get<User & { sessionExpiresAt?: string | null }>('/auth/me');
    const u = res.data;
    if (!u?.id) return 'none';
    sessionUser = u;
    sessionExpiry = u.sessionExpiresAt ? new Date(u.sessionExpiresAt) : null;
    seedContextIdentity(u.companyId ?? null);
    return 'session';
  } catch (err) {
    if (isUnavailabilityError(err)) return 'unreachable';
    clearSession();
    // The server refused the cookie (expired, or from another environment). Have it cleared, so
    // the next load finds no presence marker and asks nothing.
    apiClient.post('/auth/logout').catch(() => { /* best effort */ });
    return 'none';
  }
}

export async function rehydrateSessionAsync(): Promise<boolean> {
  return (await rehydrateSessionStateAsync()) === 'session';
}

/**
 * Headers for a hand-written fetch to the API.
 *
 * The Authorization header goes out only while a token is in memory. After a refresh there is none,
 * and sending "Bearer " with nothing behind it is worse than sending nothing: it looks like a
 * credential and is not one. Pair with credentials: 'include' so the cookie is sent either way.
 */
export function authHeaders(): Record<string, string> {
  return sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
}

/**
 * The platform console's session, kept exactly as the HRMS one is: identity and expiry in memory
 * for the page, credential in an HttpOnly cookie of its own. A platform operator may also hold an
 * HRMS login in the same browser, and the two must never answer for each other, which is why every
 * platform request carries an X-Session marker and the server picks the cookie by it.
 */
let platformSessionToken: string | null = null;
let platformSessionExpiry: Date | null = null;
let platformSessionUser: import('../types/user.types').PlatformUser | null = null;

export function setPlatformSessionToken(token: string | null, expiresAt?: string | Date | null): void {
  platformSessionToken = token || null;
  if (token && expiresAt) platformSessionExpiry = new Date(expiresAt);
  else if (!token) platformSessionExpiry = null;
}

export function getPlatformSessionExpiry(): Date | null {
  return platformSessionExpiry;
}

/** Ask the server who the platform operator is — the platform cookie goes with the request. */
export async function rehydratePlatformSessionAsync(): Promise<boolean> {
  try {
    const res = await platformApiClient.get<import('../types/user.types').PlatformUser & { sessionExpiresAt?: string | null }>('/PlatformAuth/me');
    const u = res.data;
    if (!u?.id) return false;
    platformSessionUser = u;
    platformSessionExpiry = u.sessionExpiresAt ? new Date(u.sessionExpiresAt) : null;
    return true;
  } catch (err) {
    platformSessionToken = null; platformSessionExpiry = null; platformSessionUser = null;
    if (!isUnavailabilityError(err)) platformApiClient.post('/PlatformAuth/logout', {}).catch(() => { /* best effort */ });
    return false;
  }
}

/** Headers for the platform client: the marker always, the bearer only while in memory. */
export function platformAuthHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'X-Session': 'platform' };
  if (platformSessionToken) h.Authorization = `Bearer ${platformSessionToken}`;
  return h;
}

/**
 * One claim read off the JWT at the moment it is in hand.
 *
 * is_platform used to be decoded from the stored token whenever asked. The token is no longer
 * stored, so after a reload the answer would silently become "no" and the platform links a
 * main-app operator relies on would vanish. Read once at login and kept with the identity — it is
 * a fact about who signed in, not a credential.
 */
function claimIsPlatform(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload['is_platform'] === 'true' || payload['is_platform'] === true;
  } catch { return false; }
}


/**
 * THE read of the session token.
 *
 * Fifteen files reached into localStorage for 'authToken' by name — components, hooks, services,
 * even an avatar helper — so the storage mechanism was spelled out in fifteen places. Moving the
 * session to an HttpOnly cookie, which is where it belongs, would have meant finding and correcting
 * every one of them; behind a single accessor it is one change.
 *
 * It is not a security fix on its own. The token still lives in localStorage and is still readable
 * by anything running on the page. What this buys is that the decision now has one owner.
 */
export function getAuthToken(): string | null {
  return sessionToken;
}

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const headers = credentials.companyId ? { 'X-Company-Id': String(credentials.companyId) } : {};
    const response = await apiClient.post<AuthResponse>('/auth/login', credentials, { headers });
    if (response.data.token) {
      setSessionToken(response.data.token, response.data.expiresAt);
      setSessionUser({ ...response.data.user, isPlatform: claimIsPlatform(response.data.token) } as User);
    }
    return response.data;
  },

  async platformLogin(credentials: PlatformLoginRequest): Promise<PlatformAuthResponse> {
    const response = await apiClient.post<PlatformAuthResponse>('/PlatformAuth/login', credentials);
    if (response.data.token) {
      setPlatformSessionToken(response.data.token, response.data.expiresAt);
      platformSessionUser = response.data.user;
    }
    return response.data;
  },

  /**
   * Companies an account with this username exists in, so the sign-in form can fill the Company
   * field in before the password is entered. Returns [] for an unknown username — the caller must
   * not tell the two apart in the UI. Rate-limited server-side.
   */
  async getCompaniesForUsername(username: string): Promise<CompanyLookup[]> {
    const response = await apiClient.post<CompanyLookup[]>('/auth/companies-for-username', { username });
    return response.data;
  },

  async getPublicCompanies(): Promise<CompanyLookup[]> {
    const response = await apiClient.get<CompanyLookup[]>('/companies/public-list');
    return response.data;
  },

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/register', userData);
    if (response.data.token) {
      setSessionToken(response.data.token, response.data.expiresAt);
      setSessionUser(response.data.user);
    }
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  logout(): void {
    clearSessionAndRedirect();
  },

  /** The signed-in user, from memory. Empty after a reload until rehydrateSessionAsync has run. */
  getStoredUser(): User | null {
    return sessionUser;
  },

  /**
   * True only for the hidden per-company owner account (ElectEmb). Gates destructive data-cleanup
   * actions the server also restricts to that account — the UI check is a courtesy, the API is the
   * authority.
   */
  isHiddenOwner(): boolean {
    return (this.getStoredUser()?.username ?? '').toLowerCase() === 'electemb';
  },

  isAuthenticated(): boolean {
    // Judged by the stored EXPIRY, never by whether a token is in memory. The credential is the
    // HttpOnly cookie, which this code cannot see; memory holds a copy only for the life of the
    // page. Asking "is there a token" here was the regression: a mode switch reloads the page,
    // memory came back empty, and a live session read as signed out — straight to the login page.
    //
    // A present-but-expired session must still not count — otherwise the root route would land on
    // the Dashboard with a dead session. checkOnLoad() clears it on a hard load; this guards
    // client-side navigations too. The server remains the judge: a session it rejects is a 401.
    if (!getSessionExpiry()) return false;
    return !isTokenExpired();
  },

  isPlatformAuthenticated(): boolean {
    // By stored expiry, never by the in-memory token — after a reload memory is empty while the
    // platform cookie carries on. Same rule, and same regression avoided, as isAuthenticated().
    const expiry = getPlatformSessionExpiry();
    return !!expiry && Date.now() < expiry.getTime();
  },

  /** True only when the HRMS login carried is_platform=true — read at login, kept with the identity. */
  isMainAppPlatformUser(): boolean {
    return (this.getStoredUser() as { isPlatform?: boolean } | null)?.isPlatform === true;
  },

  getStoredPlatformUser(): import('../types/user.types').PlatformUser | null {
    return platformSessionUser;
  },

  platformLogout(): void {
    // The cookie is HttpOnly, so only the server can remove it; fire-and-forget so the local
    // clear happens whether or not the API is reachable.
    try {
      void fetch(`${API_BASE_URL}/PlatformAuth/logout`, {
        method: 'POST', credentials: 'include', headers: { 'X-Session': 'platform' },
      });
    } catch { /* local clear still stands */ }
    setPlatformSessionToken(null);
    platformSessionUser = null;
    clearPlatformPreferences();
  },

  updateStoredUser(partial: Partial<import('../types/user.types').User>): void {
    if (!sessionUser) return;
    sessionUser = { ...sessionUser, ...partial };
    seedContextIdentity(sessionUser.companyId ?? null);
  },

  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data);
    return response.data;
  },

  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await apiClient.post('/auth/reset-password', data);
  },

  /** Platform-operator variants — same shape, PlatformUsers table, platform mailbox. */
  async platformForgotPassword(usernameOrEmail: string): Promise<string> {
    const res = await apiClient.post<{ message: string }>(
      '/PlatformAuth/forgot-password', { usernameOrEmail });
    return res.data.message;
  },

  async platformResetPassword(token: string, newPassword: string): Promise<string> {
    const res = await apiClient.post<{ message: string }>(
      '/PlatformAuth/reset-password', { token, newPassword });
    return res.data.message;
  },

  async switchMode(targetMode: LoginMode): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/switch-mode', { targetMode });
    if (response.data.token) {
      setSessionToken(response.data.token, response.data.expiresAt);
      setSessionUser(response.data.user);
    }
    return response.data;
  },
};
